import type { CrmTask, SalesStage } from '../../../api';
import { SALES_STAGE_META } from './crmLabels';

export type NavigatorTone = 'critical' | 'ops' | 'normal';

export type NavigatorLine = {
  tone: NavigatorTone;
  text: string;
};

const PRE_PAID_STAGES: SalesStage[] = ['lead_received', 'negotiation', 'invoice_sent'];
const OPS_NAV_HINTS: Partial<Record<SalesStage, string>> = {
  paid: 'Оплата получена. Запланируйте замер на объекте.',
  measurement: 'Замер: выезд инженера, слот и адрес с клиентом.',
  measurement_done: 'После замера: отчёт и спецификация по помещениям.',
  compose: 'Заполните состав заказа в каталоге и согласуйте смету.',
  production: 'Производство: контроль сроков изготовления.',
  ready_to_ship: 'Изделие готово — назначьте монтаж, доставку или самовывоз.',
  fulfillment: 'Выдача: согласуйте дату монтажа или доставки, затем закройте статус «Закрыт · выдан».',
};

const TASK_KIND_LABELS: Record<string, string> = {
  call: 'Звонок',
  follow_up: 'Follow-up',
  measurement: 'Замер',
  invoice: 'Счёт',
  other: 'Задача',
};

function formatDue(dueAt: string): string {
  return new Date(dueAt).toLocaleString('ru-RU', {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function pickNextOpenTask(tasks: CrmTask[]): CrmTask | null {
  const open = tasks.filter((t) => t.status === 'open' && t.due_at);
  if (!open.length) return null;
  return [...open].sort((a, b) => new Date(a.due_at).getTime() - new Date(b.due_at).getTime())[0];
}

export { pickNextOpenTask };

export function buildCrmNavigatorLine(params: {
  isLead: boolean;
  stage: SalesStage;
  productionRead: boolean;
  tasks: CrmTask[];
  hasValidOpenTask: boolean;
  hasOverdueTask: boolean;
}): NavigatorLine | null {
  const { isLead, stage, productionRead, tasks, hasValidOpenTask, hasOverdueTask } = params;

  if (productionRead) {
    return {
      tone: 'ops',
      text: 'Режим цеха: открыты операции и производство. Продажи и чат — только для просмотра.',
    };
  }

  const salesStage = PRE_PAID_STAGES.includes(stage) || isLead;
  if (salesStage && (!hasValidOpenTask || hasOverdueTask)) {
    return {
      tone: 'critical',
      text: '⚠️ Сделка без задачи! Запланируйте следующий шаг, чтобы не потерять клиента.',
    };
  }

  if (!isLead && OPS_NAV_HINTS[stage]) {
    return {
      tone: 'ops',
      text: OPS_NAV_HINTS[stage]!,
    };
  }

  const next = pickNextOpenTask(tasks);
  if (next) {
    const kind = TASK_KIND_LABELS[next.kind] || next.kind || 'Задача';
    return {
      tone: 'normal',
      text: `Следующий шаг: ${kind} до ${formatDue(next.due_at)}`,
    };
  }

  const stageLabel = SALES_STAGE_META[stage]?.label;
  if (stageLabel) {
    return {
      tone: 'normal',
      text: `Этап: ${stageLabel}`,
    };
  }
  return null;
}

export function navigatorLineColor(tone: NavigatorTone): string {
  if (tone === 'critical') return 'var(--orange, #FC814A)';
  if (tone === 'ops') return '#68D391';
  return 'var(--text-3)';
}
